// Fetch wrapper for API calls.
// Credentials included so session cookies flow. Errors parsed into the platform envelope.

const DEFAULT_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

/**
 * API error thrown for non-2xx responses. Carries the server envelope fields
 * ({ error, message, status }) plus any extra fields like `fields`.
 */
export class ApiError extends Error {
  constructor({ error, message, status, fields, details }) {
    super(message || error || 'Request failed');
    this.name = 'ApiError';
    this.code = error;
    this.status = status;
    if (fields) this.fields = fields;
    if (details) this.details = details;
  }
}

async function parseResponse(res) {
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Low-level request function. Prefer `api.get` / `api.post` etc. below.
 *
 * @param {string} path — starts with `/api/...` or `/auth/...`
 * @param {object} [opts]
 * @param {string} [opts.method='GET']
 * @param {object} [opts.body] — serialized as JSON
 * @param {object} [opts.headers]
 * @param {AbortSignal} [opts.signal]
 */
export async function request(path, opts = {}) {
  const { method = 'GET', body, headers = {}, signal } = opts;
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: { ...DEFAULT_HEADERS, ...headers },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  const data = await parseResponse(res);

  if (!res.ok) {
    throw new ApiError({
      error: data?.error || 'request_failed',
      message: data?.message || res.statusText,
      status: data?.status || res.status,
      fields: data?.fields,
      details: data?.details,
    });
  }

  return data;
}

export const api = {
  get: (path, opts) => request(path, { ...opts, method: 'GET' }),
  post: (path, body, opts) => request(path, { ...opts, method: 'POST', body }),
  patch: (path, body, opts) => request(path, { ...opts, method: 'PATCH', body }),
  put: (path, body, opts) => request(path, { ...opts, method: 'PUT', body }),
  delete: (path, opts) => request(path, { ...opts, method: 'DELETE' }),
};
